import { useRef, useState, useMemo, useCallback } from 'react';
import throttle from 'lodash/throttle';
import type { ColumnsType } from 'antd/es/table';
import type { ResizeCallbackData } from 'react-resizable';

const useResizableColumns = <T>(
  columns: ColumnsType<T>,
  throttleTime = 1000
) => {
  const columnWidths = useRef<Record<number, number>>({});
  const [, setForceRender] = useState({});

  // Properly memoize the throttled function
  const throttledSetForceRender = useMemo(
    () => throttle(() => setForceRender({}), throttleTime),
    [throttleTime]
  );

  // Use callback to memoize handleResize
  const handleResize = useCallback(
    (index: number) =>
      (_: React.SyntheticEvent<Element>, { size }: ResizeCallbackData) => {
        const newWidth = size.width;
        if (columnWidths.current[index] !== newWidth) {
          columnWidths.current[index] = newWidth;
          throttledSetForceRender();
        }
      },
    [throttledSetForceRender]
  );

  // Memoize mergedColumns
  const mergedColumns = useMemo(
    () =>
      columns.map((col, index) => {
        if ('children' in col) return col;

        return {
          ...col,
          width:
            columnWidths.current[index] ??
            (typeof col.width === 'number' ? col.width : undefined),
          onHeaderCell: () => ({
            width:
              columnWidths.current[index] ??
              (typeof col.width === 'number' ? col.width : undefined),
            onResize: handleResize(index) as React.ReactEventHandler,
          }),
        };
      }),
    [columns, handleResize]
  );

  return mergedColumns;
};

export default useResizableColumns;
